import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import Page from '../../components/ora/Page';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';
import MSHeader from '../../components/ora/NonAuth/MSHeader';
import MSFooter from '../../components/ora/NonAuth/MSFooter';
import NonAuthPage from '../../components/ora/NonAuth/NonAuthPage';
import { useLocation, useNavigate } from 'react-router-dom';
import PricingOption from '../../components/ora/Pricing/PricingOption';
import {
  SubscriptionPlan,
  SubscriptionAccess,
  AnnualBilling,
  SubscriptionStatus,
  hasComplimentaryOnlyAccess,
} from '../../types/Subscription';
import {
  selectAccessLevel,
  selectSubscription,
} from '../../slices/subscriptions/subscriptionsSlice';
import {
  getSubscriptionStatus,
  handleCheckout,
  handleOneTimeCheckout,
  ONE_TIME_ACCESS_SKU_FIVE_YEAR,
  fetchPublicCohortDiscount,
  fetchPublicMarketingOfferDiscount,
  fetchPublicUniversalDiscount,
  PromotionCodePreview,
} from '../../services/subscriptions';
import { DiscountResponse } from '../../services/subscriptions';
import {
  getFreeFeatures,
  getProFeatures,
  ORA_PRO_5YEAR_PRICE,
  ORA_PRO_5YEAR_YEARS,
  ORA_PRO_ANNUAL_PRICE,
  ORA_PRO_MONTHLY_PRICE,
  PricingFeature,
} from '../../constants/pricingTiers';
import TrialTimeline from '../../components/ora/Pricing/TrialTimeline/TrialTimeline';
import {
  formatPrice,
  formatTrialCtaLabel,
  getPlanDiscount,
  getPlanTrialPeriodDays,
  getSharedExplicitTrialPeriodDays,
  getSharedTrialPeriodDays,
  shouldShowPlanTrialMessaging,
} from '../../utils/pricing';
import { Browser } from '@capacitor/browser';
import { useDarkMode } from '../../hooks/useDarkMode';
import OraGuarantee from '../../assets/svgs/og.svg';

import { hapticsImpactLight } from '../../utils/haptics';
import { showIntercom } from '../../utils/intercom';
import { Check, ArrowRight } from '@phosphor-icons/react';
import DiscountBanner from '../../components/ora/Global/DiscountBanner';
import DiscountCountdownCard from '../../components/ora/Global/DiscountCountdown/DiscountCountdownCard';
import LightStreak from '../../assets/images/light-streak.png';
import { capture } from '../../utils/analytics';
import { UserRole } from '../../types/User';
import { getCookie } from '../../utils/getCookie';
import {
  appendAttributionToAuthQueryParams,
  resolveCurrentVisitAttribution,
  resolveMarketingOfferKey,
} from '../../utils/attribution';
import { claimMarketingOfferKey } from '../../services/auth';
import { loadActiveDiscount } from '../../slices/discount/discountSlice';
import { getSchoolLaunchDeadline } from '../../utils/discountExpiry';
import { useDiscountCountdownVariant } from '../../utils/discountCountdownExperiment';
import { store } from '../../store/store';
import { Capacitor } from '@capacitor/core';
import { useAccess } from '@/hooks/useAccess';
import { usePrimaryExamType } from '@/hooks/usePrimaryExamType';
import PromoCodeInput from '../../components/ora/Pricing/PromoCodeInput/PromoCodeInput';
import {
  EffectivePlanPricing,
  getMonthlyPostPromotionLabel,
  getPromotionCodeForPlan,
  getTrialPricePresentation,
  PricingCadence,
  resolveEffectivePlanPricing,
} from './promotionCode';
import PlanTileRedesign from './PlanTileRedesign';
import {
  cleanDiscountName,
  getSavingsVsMonthlyPercent,
} from './redesignPresentation';
import {
  getPricingPageVariantOverride,
  PricingPageVariant,
  readPricingPageVariant,
} from '../../utils/pricingPageExperiment';
import { PlanDiscount } from '../../services/subscriptions';

const FREE_ACCESS_ROLES = new Set<UserRole>([
  UserRole.free_access,
  UserRole.admin,
  UserRole.qa,
  UserRole.qa_qbank,
  UserRole.qa_flashcards,
]);

// Feature list rendering (shared by the Free and Ora Pro cards). Content lives
// in src/constants/pricingTiers so copy is edited in one place.
const renderFeatureItem = (feature: PricingFeature) => (
  <li key={feature.label}>
    <span>
      <Check /> {feature.label}
    </span>
    {feature.subItems && (
      <ul className="sub-list">
        {feature.subItems.map((subItem) => (
          <li key={subItem}>{subItem}</li>
        ))}
      </ul>
    )}
  </li>
);

const renderEverythingInFreeItem = () => (
  <li>
    <span>
      <Check /> Everything in Free
    </span>
  </li>
);

const renderGuaranteeItem = () => (
  <li className="guarantee-item">
    <Check />
    <button
      onClick={() => {
        const guaranteeElement = document.getElementById('guarantee');

        if (guaranteeElement) {
          guaranteeElement.scrollIntoView({
            behavior: 'smooth',
          });
        }
      }}
    >
      <img
        className="ora-guarantee"
        src={OraGuarantee}
        alt="Ora Guarantee"
        aria-label="Ora Guarantee"
      />
    </button>
  </li>
);

interface PricingProps {
  embedded?: boolean;
  skipFunction?: () => void;
}

const Pricing: React.FC<PricingProps> = ({
  embedded = false,
  skipFunction,
}) => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  const currentUser = useSelector((state: RootState) => state.user.profile);
  const subscription = useSelector(selectSubscription);
  const accessLevel = useSelector(selectAccessLevel);
  const { isGated } = useAccess();
  // An NCLEX-RN (nursing) primary gets the nursing feature lists and no UWorld
  // Step guarantee (ORA-952 D5). Logged-out visitors and every medical exam
  // keep the medical copy.
  const { isNclex } = usePrimaryExamType();
  const freeFeatures = getFreeFeatures(isNclex);
  const proFeatures = getProFeatures(isNclex);

  const proFeaturesWithSubItems = proFeatures.filter(
    (feature) => feature.subItems
  );

  // First-time subscribers get an opt-out free trial (ORA-33): $0 today,
  // full Pro access, auto-charged when it ends unless they cancel. The backend
  // is the source of truth for eligibility (trial granted only to users with no
  // prior subscription); here we surface trial-forward copy to the acquisition
  // audience. A churned/canceled user shows accessLevel FREE but keeps a
  // non-FREE subscription status, so we exclude them — the backend won't grant
  // them a repeat trial and we shouldn't advertise one. Logged-out visitors
  // always see it (Stripe checkout shows the true terms before payment anyway).
  const hasPriorSubscription =
    !!subscription?.status && subscription.status !== SubscriptionStatus.FREE;

  const userActiveDiscount = useSelector(
    (state: RootState) => state.discount.data as DiscountResponse | null
  );

  const showTrialMessaging =
    !isAuthenticated ||
    (accessLevel === SubscriptionAccess.FREE &&
      !hasPriorSubscription &&
      userActiveDiscount?.isTrialEligible !== false);

  const [showMarketingSiteStyles] = useState(false);
  const [toggleAnnualPricing, setToggleAnnualPricing] = useState(false);

  const [promotionCodePreview, setPromotionCodePreview] =
    useState<PromotionCodePreview | null>(null);

  const [promotionCheckoutError, setPromotionCheckoutError] = useState<
    string | null
  >(null);

  const [cohort, setCohort] = useState('');

  const [publicCohortDiscount, setPublicCohortDiscount] =
    useState<DiscountResponse | null>(null);

  // Pricing tiles A/B test (ORA-392): `control` renders the current tiles,
  // `redesign` the price-forward B side. Starts as control and flips only
  // once PostHog resolves the flag (exposure fires on that read).
  const [pricingPageVariant, setPricingPageVariant] =
    useState<PricingPageVariant | null>(null);

  const activeDiscount = isAuthenticated
    ? userActiveDiscount
    : publicCohortDiscount;

  const location = useLocation();
  const navigate = useNavigate();
  useDarkMode();

  // Handle Capacitor Browser close
  useEffect(() => {
    if (embedded || !Capacitor.isNativePlatform()) {
      return;
    }

    const handleBrowserClose = async () => {
      await getSubscriptionStatus();
      if (window.location.pathname !== '/') {
        navigate('/');
      }
    };

    const listenerPromise = Browser.addListener(
      'browserFinished',
      handleBrowserClose
    );

    return () => {
      void listenerPromise.then((listener) => listener.remove());
    };
  }, [embedded, navigate]);

  // Handle Capacitor Browser close
  useEffect(() => {
    if (currentUser && !currentUser.email) {
      navigate('/');
    }
  }, [currentUser?.id]);

  // If User Has Subscription, Redirect to Home. Users whose access comes only
  // from a complimentary grant stay — they need this page to (re)subscribe.
  useEffect(() => {
    if (
      isAuthenticated &&
      (subscription?.status === SubscriptionStatus.ACTIVE ||
        (!isGated() && !hasComplimentaryOnlyAccess(subscription)))
    ) {
      navigate('/');
    }
  }, [isAuthenticated, subscription?.status]);

  useEffect(() => {
    document.body.classList.add('pricing-page');
    return () => {
      document.body.classList.remove('pricing-page');
    };
  }, []);

  useEffect(() => {
    if (
      accessLevel === SubscriptionAccess.PRE_CLINICAL ||
      accessLevel === SubscriptionAccess.STEP_SHELF
    ) {
      if (subscription?.plan === SubscriptionPlan.ANNUAL) {
        setToggleAnnualPricing(true);
      } else {
        setToggleAnnualPricing(false);
      }
    }
  }, [subscription, isAuthenticated]);

  useEffect(() => {
    const resolvedMarketingOfferKey = resolveMarketingOfferKey();

    if (
      !isAuthenticated ||
      !resolvedMarketingOfferKey ||
      currentUser?.marketingOfferKey === resolvedMarketingOfferKey
    ) {
      return;
    }

    let isMounted = true;

    const claimOffer = async () => {
      const claimedOfferKey = await claimMarketingOfferKey(
        resolvedMarketingOfferKey
      );

      if (!isMounted || !claimedOfferKey) return;
      store.dispatch(loadActiveDiscount());
    };

    void claimOffer();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.marketingOfferKey, isAuthenticated, location.search]);

  useEffect(() => {
    if (isAuthenticated) {
      setPublicCohortDiscount(null);
      return;
    }

    const params = new URLSearchParams(location.search);
    const cohortParam = params.get('cohort');
    const storedCohort = localStorage.getItem('ora-cohort');
    const cohortCookie = getCookie('ora-cohort');
    const resolvedCohort = cohortParam || storedCohort || cohortCookie || '';

    if (cohortParam) {
      localStorage.setItem('ora-cohort', cohortParam);
    }

    setCohort(resolvedCohort);

    const resolvedMarketingOfferKey = resolvedCohort
      ? ''
      : (resolveMarketingOfferKey() ?? '');

    let isMounted = true;

    const loadPublicDiscount = async () => {
      // Targeted (cohort / marketing-offer) discount takes precedence; otherwise
      // fall back to the universal "summer sale" so every logged-out visitor
      // sees sale pricing (ORA-44). Authoritative eligibility is re-checked at
      // checkout server-side.
      let discount: DiscountResponse | null = null;
      if (resolvedCohort) {
        discount = await fetchPublicCohortDiscount(resolvedCohort);
      } else if (resolvedMarketingOfferKey) {
        discount = await fetchPublicMarketingOfferDiscount(
          resolvedMarketingOfferKey
        );
      }

      if (!discount) {
        discount = await fetchPublicUniversalDiscount();
      }

      if (isMounted) {
        setPublicCohortDiscount(discount);
      }
    };

    void loadPublicDiscount();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, location.search]);

  // Resolve the A/B variant exactly once (ORA-392). The QA override skips
  // PostHog entirely; otherwise the flag read fires the exposure event, so
  // bucketing only happens for visitors who actually saw the pricing page.
  // /pricing is a public route, so on an authed hard load the profile arrives
  // after mount: wait for it before reading, or the flag would be evaluated
  // on the anonymous id and then re-hashed after identify — flipping tiles
  // mid-view and double-exposing the user. The `pricingPageVariant !== null`
  // guard makes the first successful read final.
  useEffect(() => {
    if (pricingPageVariant !== null) return;

    const override = getPricingPageVariantOverride();
    if (override) {
      setPricingPageVariant(override);
      return;
    }

    if (isAuthenticated && !currentUser?.id) return;

    let isMounted = true;

    void readPricingPageVariant(currentUser?.id).then((variant) => {
      if (isMounted) setPricingPageVariant(variant);
    });

    return () => {
      isMounted = false;
    };
  }, [pricingPageVariant, isAuthenticated, currentUser?.id]);

  // Discount countdown experiment (ORA-892): authed launch-cohort students
  // whose School Launch discount has a future phase deadline may see a
  // countdown card in the header. The variant is read only once that surface
  // is genuinely renderable, so the exposure matches what was on screen. The
  // logged-out page (public cohort discount) is out of scope: the experiment
  // targets students already in the app.
  const discountCountdownDeadline = useMemo(
    () => (isAuthenticated ? getSchoolLaunchDeadline(activeDiscount) : null),
    [isAuthenticated, activeDiscount]
  );

  const discountCountdownVariant = useDiscountCountdownVariant({
    userId: currentUser?.id,
    enabled: isAuthenticated && discountCountdownDeadline !== null,
  });

  const showDiscountCountdown =
    discountCountdownVariant === 'test' && discountCountdownDeadline !== null;

  // Conversion events carry the variant the user actually SAW (not their
  // PostHog assignment): until the flag resolves the control tiles render,
  // so events in that window are stamped `control` even if PostHog later
  // buckets the user into `redesign`. Analyze assignment via the auto-tagged
  // `$feature/...` property; use this one for what was on screen. The same
  // applies to the discount countdown variant.
  const capturePricing = (event: string, props: Record<string, unknown>) =>
    capture(event, {
      ...props,
      pricingVariant: pricingPageVariant ?? 'control',
      discountCountdownVariant: discountCountdownVariant ?? 'control',
    });

  const startCheckout = async (
    checkout: () => Promise<unknown>,
    plan: 'MONTHLY' | 'ANNUAL' | 'FIVE_YEAR',
    hasCustomPromotion: boolean
  ) => {
    setPromotionCheckoutError(null);
    try {
      await checkout();
    } catch {
      if (!hasCustomPromotion) return;

      setPromotionCodePreview(null);

      setPromotionCheckoutError(
        'That promo code could not be applied at checkout. Please apply it again or use the automatic discount.'
      );

      capturePricing('pricing_promo_code_checkout_failed', { plan });
    }
  };

  const renderProCheckoutButton = (
    plan: SubscriptionPlan,
    product: SubscriptionAccess
  ) => {
    // The CTA has to agree with the price block above it, which uses the same
    // per-plan gate.
    const showPlanTrialMessaging =
      plan === SubscriptionPlan.ANNUAL
        ? showAnnualTrialMessaging
        : showMonthlyTrialMessaging;

    const trialCtaLabel = formatTrialCtaLabel(
      plan === SubscriptionPlan.ANNUAL
        ? annualTrialPeriodDays
        : monthlyTrialPeriodDays
    );

    const renderCheckoutButtonText = () => {
      if (isAuthenticated) {
        if (currentUser?.role && FREE_ACCESS_ROLES.has(currentUser.role)) {
          return 'Free Access';
        }
        // Free User (No Active Subscription)
        if (accessLevel === SubscriptionAccess.FREE) {
          return showPlanTrialMessaging ? trialCtaLabel : 'Get Started';
        } else {
          return subscription?.plan === plan ? 'Current Plan' : 'Get Started';
        }
      } else {
        return showPlanTrialMessaging ? trialCtaLabel : 'Get Started';
      }
    };

    const disableCheckoutButton = accessLevel === SubscriptionAccess.STEP_SHELF;

    return (
      <button
        className="button button--get-started button--annual"
        onClick={() => {
          if (isAuthenticated) {
            if (plan === SubscriptionPlan.ANNUAL) {
              const annualBilling = AnnualBilling.BILLED_YEARLY;

              capturePricing('pricing_option_clicked', {
                product,
                plan,
                annualBilling,
              });

              capturePricing('checkout_start', {
                product,
                plan,
                annualBilling,
                source: 'pricing_page',
                hasCustomPromotion: !!getPromotionCodeForPlan(
                  promotionCodePreview,
                  'ANNUAL'
                ),
              });

              const promotionCode = getPromotionCodeForPlan(
                promotionCodePreview,
                'ANNUAL'
              );

              void startCheckout(
                () =>
                  handleCheckout(
                    product,
                    plan,
                    annualBilling,
                    undefined,
                    undefined,
                    promotionCode
                  ),
                'ANNUAL',
                !!promotionCode
              );
            } else {
              capturePricing('pricing_option_clicked', {
                product,
                plan,
              });

              capturePricing('checkout_start', {
                product,
                plan,
                source: 'pricing_page',
                hasCustomPromotion: !!getPromotionCodeForPlan(
                  promotionCodePreview,
                  'MONTHLY'
                ),
              });

              const promotionCode = getPromotionCodeForPlan(
                promotionCodePreview,
                'MONTHLY'
              );

              void startCheckout(
                () =>
                  handleCheckout(
                    product,
                    plan,
                    undefined,
                    undefined,
                    undefined,
                    promotionCode
                  ),
                'MONTHLY',
                !!promotionCode
              );
            }
          } else {
            const params = new URLSearchParams();
            params.set('signup', 'true');
            params.set('product', product);
            params.set('plan', plan);
            const { attribution } = resolveCurrentVisitAttribution();

            if (plan === SubscriptionPlan.ANNUAL) {
              const annualBilling = AnnualBilling.BILLED_YEARLY;

              capturePricing('pricing_option_clicked', {
                product,
                plan,
                annualBilling,
              });

              params.set('annualBilling', annualBilling);
            }

            if (cohort) {
              params.set('cohort', cohort);
            }
            appendAttributionToAuthQueryParams(params, attribution);

            navigate(`/auth?${params.toString()}`);
          }
        }}
        disabled={disableCheckoutButton}
      >
        {renderCheckoutButtonText()}
      </button>
    );
  };

  //  ORA PRO PRICING LOGIC
  // Prices, trial length, and price helpers live in src/constants/pricingTiers
  // and src/utils/pricing so the Pricing page and the /unlock PlanSelector share
  // one source of truth.

  // Trial-forward price block (ORA-37): lead with "$0 due today", then a pill
  // naming the first post-trial charge ("then $99/mo"). A temporary recurring
  // discount carries its duration in the pill ("for 3 months") and the regular
  // rate that follows the discount is not named (ORA-377). Student Life
  // remains one payment. The trial length lives only in the CTA ("Start 10
  // Day Free Trial"), not in a note here (ORA-384).
  const renderTrialPriceBlock = (
    pricing: EffectivePlanPricing,
    cadence: PricingCadence
  ) => {
    const presentation = getTrialPricePresentation(pricing, cadence);
    const hasDiscount = pricing.isDiscountActive;

    return (
      <>
        <div className="pricing-option_inner_top_price_amount">
          <div className="price-discounted">
            <span className="dollar-sign">$</span>0
          </div>
        </div>
        <div className="pricing-option_inner_top_price_label">due today</div>
        <div className="price-pill">
          {presentation.pricePrefix && `${presentation.pricePrefix} `}
          {hasDiscount && (
            <span className="price-pill_original">
              ${formatPrice(pricing.originalPrice)}
            </span>
          )}
          ${formatPrice(pricing.discountedPrice)}
          <span className="price-pill_unit">{presentation.unitLabel}</span>
          {presentation.priceSuffix && ` ${presentation.priceSuffix}`}
        </div>
      </>
    );
  };

  const renderOneTimeCheckoutButton = () => {
    // Block users who already have Pro access (STEP_SHELF subscribers,
    // institutional, existing one-time purchasers) AND anyone on an active
    // recurring plan (e.g. legacy PRE_CLINICAL) so they don't double-pay.
    const isCurrentlyPro =
      accessLevel === SubscriptionAccess.STEP_SHELF ||
      subscription?.status === SubscriptionStatus.ACTIVE;

    const startText = showFiveYearTrialMessaging
      ? formatTrialCtaLabel(fiveYearTrialPeriodDays)
      : 'Get Started';

    const buttonText = isAuthenticated
      ? currentUser?.role && FREE_ACCESS_ROLES.has(currentUser.role)
        ? 'Free Access'
        : isCurrentlyPro
          ? 'Current Plan'
          : startText
      : startText;

    return (
      <button
        className="button button--get-started"
        disabled={isCurrentlyPro}
        onClick={() => {
          if (isAuthenticated) {
            capturePricing('pricing_option_clicked', {
              product: SubscriptionAccess.STEP_SHELF,
              oneTimeAccessSku: ONE_TIME_ACCESS_SKU_FIVE_YEAR,
            });

            capturePricing('checkout_start', {
              product: SubscriptionAccess.STEP_SHELF,
              oneTimeAccessSku: ONE_TIME_ACCESS_SKU_FIVE_YEAR,
              source: 'pricing_page',
              hasCustomPromotion: !!getPromotionCodeForPlan(
                promotionCodePreview,
                'FIVE_YEAR'
              ),
            });

            const promotionCode = getPromotionCodeForPlan(
              promotionCodePreview,
              'FIVE_YEAR'
            );

            void startCheckout(
              () =>
                handleOneTimeCheckout(
                  ONE_TIME_ACCESS_SKU_FIVE_YEAR,
                  undefined,
                  promotionCode
                ),
              'FIVE_YEAR',
              !!promotionCode
            );
          } else {
            const params = new URLSearchParams();
            params.set('signup', 'true');
            params.set('oneTimeSku', ONE_TIME_ACCESS_SKU_FIVE_YEAR);
            const { attribution } = resolveCurrentVisitAttribution();

            capturePricing('pricing_option_clicked', {
              product: SubscriptionAccess.STEP_SHELF,
              oneTimeAccessSku: ONE_TIME_ACCESS_SKU_FIVE_YEAR,
            });

            if (cohort) {
              params.set('cohort', cohort);
            }
            appendAttributionToAuthQueryParams(params, attribution);
            navigate(`/auth?${params.toString()}`);
          }
        }}
      >
        {buttonText}
      </button>
    );
  };

  // Per-plan discounts (ORA-44): each plan can carry a different coupon, so we
  // resolve monthly / annual / 5-year independently.
  const monthlyPlanDiscount = getPlanDiscount(activeDiscount, 'MONTHLY');
  const annualPlanDiscount = getPlanDiscount(activeDiscount, 'ANNUAL');
  const fiveYearPlanDiscount = getPlanDiscount(activeDiscount, 'FIVE_YEAR');

  const monthlyTrialPeriodDays = getPlanTrialPeriodDays(
    activeDiscount,
    'MONTHLY'
  );

  const annualTrialPeriodDays = getPlanTrialPeriodDays(
    activeDiscount,
    'ANNUAL'
  );

  const fiveYearTrialPeriodDays = getPlanTrialPeriodDays(
    activeDiscount,
    'FIVE_YEAR'
  );

  const sharedTrialPeriodDays = getSharedTrialPeriodDays(activeDiscount);

  // A discount rule can grant a trial the legacy `showTrialMessaging` gate
  // hides (it excludes every churned user, but the backend grants a rule's
  // trial override to any non-elevated user). An explicit per-plan
  // trialPeriodDays in the payload is the backend confirming THIS user gets
  // THAT trial at checkout, so it re-enables the trial copy for that plan.
  const showMonthlyTrialMessaging = shouldShowPlanTrialMessaging(
    showTrialMessaging,
    activeDiscount,
    'MONTHLY'
  );

  const showAnnualTrialMessaging = shouldShowPlanTrialMessaging(
    showTrialMessaging,
    activeDiscount,
    'ANNUAL'
  );

  const showFiveYearTrialMessaging = shouldShowPlanTrialMessaging(
    showTrialMessaging,
    activeDiscount,
    'FIVE_YEAR'
  );

  // The timeline is plan-agnostic, so it needs one duration that holds for
  // every plan: the standard resolution when the gate is open, and otherwise
  // only an explicit override shared by all three plans.
  const timelineTrialPeriodDays = showTrialMessaging
    ? sharedTrialPeriodDays
    : getSharedExplicitTrialPeriodDays(activeDiscount);

  const monthlyPricing = resolveEffectivePlanPricing(
    ORA_PRO_MONTHLY_PRICE,
    monthlyPlanDiscount,
    promotionCodePreview?.plans.MONTHLY
  );

  const annualPricing = resolveEffectivePlanPricing(
    ORA_PRO_ANNUAL_PRICE,
    annualPlanDiscount,
    promotionCodePreview?.plans.ANNUAL
  );

  const fiveYearPricing = resolveEffectivePlanPricing(
    ORA_PRO_5YEAR_PRICE,
    fiveYearPlanDiscount,
    promotionCodePreview?.plans.FIVE_YEAR
  );

  const isMonthlyDiscountActive = monthlyPricing.isDiscountActive;
  const isAnnualDiscountActive = annualPricing.isDiscountActive;
  const isFiveYearDiscountActive = fiveYearPricing.isDiscountActive;
  const displayMonthlyPrice = monthlyPricing.discountedPrice;
  const displayAnnualPrice = annualPricing.discountedPrice;
  const displayFiveYearPrice = fiveYearPricing.discountedPrice;

  const hasAutomaticDiscount = [
    monthlyPlanDiscount,
    annualPlanDiscount,
    fiveYearPlanDiscount,
  ].some((discount) => discount != null);

  //  REDESIGNED TILES (B side of the pricing A/B test, ORA-392)

  const isRedesignVariant = pricingPageVariant === 'redesign';

  // Campaign name for the discount pill. For automatic discounts prefer the
  // campaign-level `source.name` ("DUQCOM Special Offer", "Summer Sale") —
  // it is human-curated and identical for every plan, so the three tiles show
  // one consistent label. Per-plan names are raw Stripe coupon names
  // ("School Launch Offer — 50% MONTHLY") and are only a cleaned-up fallback
  // for payloads without a source. A custom promo code is the user's own
  // input and is shown verbatim.
  const getDiscountNameForPlan = (
    pricing: EffectivePlanPricing,
    planDiscount: PlanDiscount | null
  ): string | null =>
    pricing.isCustomPromotion
      ? (promotionCodePreview?.normalizedCode ?? null)
      : (cleanDiscountName(activeDiscount?.source?.name ?? null) ??
        cleanDiscountName(planDiscount?.name ?? null));

  const annualSavingsVsMonthly = getSavingsVsMonthlyPercent(
    annualPricing,
    monthlyPricing,
    12
  );

  const fiveYearSavingsVsMonthly = getSavingsVsMonthlyPercent(
    fiveYearPricing,
    monthlyPricing,
    ORA_PRO_5YEAR_YEARS * 12
  );

  const renderRedesignTiles = () => (
    <div className="pricing-option_inner_top">
      <PlanTileRedesign
        title="Ora Pro Monthly"
        cadence="monthly"
        pricing={monthlyPricing}
        discountName={getDiscountNameForPlan(
          monthlyPricing,
          monthlyPlanDiscount
        )}
        savingsVsMonthlyPercent={null}
        showTrialFinePrint={showMonthlyTrialMessaging}
        cta={renderProCheckoutButton(
          SubscriptionPlan.MONTHLY,
          SubscriptionAccess.STEP_SHELF
        )}
      />
      <PlanTileRedesign
        title="Ora Pro Annual"
        cadence="annual"
        pricing={annualPricing}
        discountName={getDiscountNameForPlan(annualPricing, annualPlanDiscount)}
        savingsVsMonthlyPercent={annualSavingsVsMonthly}
        showTrialFinePrint={showAnnualTrialMessaging}
        cta={renderProCheckoutButton(
          SubscriptionPlan.ANNUAL,
          SubscriptionAccess.STEP_SHELF
        )}
      />
      <PlanTileRedesign
        title="Ora Pro Student Life"
        headerTag={`${ORA_PRO_5YEAR_YEARS}-yr access`}
        cadence="one_time"
        pricing={fiveYearPricing}
        accessYears={ORA_PRO_5YEAR_YEARS}
        discountName={getDiscountNameForPlan(
          fiveYearPricing,
          fiveYearPlanDiscount
        )}
        savingsVsMonthlyPercent={fiveYearSavingsVsMonthly}
        showTrialFinePrint={showFiveYearTrialMessaging}
        cta={renderOneTimeCheckoutButton()}
      />
    </div>
  );

  const showPromoCodeInput = isAuthenticated && !Capacitor.isNativePlatform();

  const renderSaleBadge = (isActive: boolean, label: string | null) =>
    isActive && label ? <div className="sale-badge">{label}</div> : null;

  // A `forever` discount (e.g. the Summer Sale) is the ongoing price — there is
  // no "reverts afterwards" copy. Temporary discounts keep that framing.
  const isMonthlyDiscountForever = monthlyPricing.durationType === 'forever';
  const isAnnualDiscountForever = annualPricing.durationType === 'forever';
  const monthlyDiscountMonths = monthlyPricing.durationMonths;

  const monthlyAfterDiscountLabel = getMonthlyPostPromotionLabel(
    monthlyPricing.durationType,
    monthlyDiscountMonths
  );

  const renderPricing = () => {
    return (
      <div className="pricing">
        <div className="pricing_header">
          <h1 className="font-gradient">Precision education for healthcare</h1>
          {showDiscountCountdown && (
            <DiscountCountdownCard
              className="mx-4 w-[calc(100%-2rem)] max-w-xl"
              deadline={discountCountdownDeadline}
              offerName={activeDiscount?.source?.name || 'Your offer'}
              onExpired={() => store.dispatch(loadActiveDiscount())}
            />
          )}
          {timelineTrialPeriodDays != null && (
            <TrialTimeline
              className="mx-4 w-[calc(100%-2rem)] max-w-xl"
              trialPeriodDays={timelineTrialPeriodDays}
            />
          )}
        </div>
        <div className="pricing_body">
          <PricingOption
            isAuthenticated={isAuthenticated}
            product={SubscriptionAccess.FREE}
            plan={
              toggleAnnualPricing
                ? SubscriptionPlan.ANNUAL
                : SubscriptionPlan.MONTHLY
            }
            title="Free"
            description="Discover Ora. Free forever."
            monthlyPrice="0"
            annualPrice="0"
            features={<>{freeFeatures.map(renderFeatureItem)}</>}
            savings=""
            savingPercent=""
            isGroupOption={false}
            guarantee={false}
            activeDiscount={activeDiscount}
            setUpgradeModalOpen={() => {}}
          />
          <div
            className={`pricing-option pricing-option--primary ${currentUser ? ' pricing-option--authed' : ''}`}
          >
            <div className="pricing-option_inner">
              {isRedesignVariant ? (
                renderRedesignTiles()
              ) : (
                <div className="pricing-option_inner_top">
                  <div className="glass-wrap relative">
                    <h2>Ora Pro Monthly</h2>
                    <div className="pricing-option_inner_top_price">
                      {renderSaleBadge(
                        isMonthlyDiscountActive,
                        monthlyPricing.badgeLabel
                      )}
                      {showMonthlyTrialMessaging ? (
                        renderTrialPriceBlock(monthlyPricing, 'monthly')
                      ) : (
                        <>
                          <div className="pricing-option_inner_top_price_amount">
                            {isMonthlyDiscountActive ? (
                              <div className="discounted-price-container">
                                <div className="price-original">
                                  ${formatPrice(monthlyPricing.originalPrice)}
                                </div>
                                <div className="price-discounted">
                                  <span className="dollar-sign">$</span>
                                  {formatPrice(displayMonthlyPrice)}
                                </div>
                              </div>
                            ) : (
                              <div className="price-discounted">
                                <span className="dollar-sign">$</span>
                                {formatPrice(monthlyPricing.originalPrice)}
                              </div>
                            )}
                          </div>
                          <div className="pricing-option_inner_top_price_label">
                            {isMonthlyDiscountActive &&
                            !isMonthlyDiscountForever
                              ? monthlyDiscountMonths &&
                                monthlyDiscountMonths > 1
                                ? `for ${monthlyDiscountMonths} months`
                                : 'this month'
                              : 'per month'}
                          </div>
                          {isMonthlyDiscountActive &&
                            !isMonthlyDiscountForever && (
                              <div className="savings">
                                <p>
                                  ${formatPrice(monthlyPricing.originalPrice)}{' '}
                                  per month {monthlyAfterDiscountLabel}
                                </p>
                              </div>
                            )}
                        </>
                      )}
                      {renderProCheckoutButton(
                        SubscriptionPlan.MONTHLY,
                        SubscriptionAccess.STEP_SHELF
                      )}
                    </div>
                  </div>
                  <div className="glass-wrap relative">
                    <h2>Ora Pro Annual</h2>
                    <div className="pricing-option_inner_top_price">
                      {renderSaleBadge(
                        isAnnualDiscountActive,
                        annualPricing.badgeLabel
                      )}
                      {showAnnualTrialMessaging ? (
                        renderTrialPriceBlock(annualPricing, 'annual')
                      ) : (
                        <>
                          <div className="pricing-option_inner_top_price_amount">
                            {isAnnualDiscountActive ? (
                              <div className="discounted-price-container">
                                <div className="price-original">
                                  ${formatPrice(annualPricing.originalPrice)}
                                </div>
                                <div className="price-discounted">
                                  <span className="dollar-sign">$</span>
                                  {formatPrice(displayAnnualPrice)}
                                </div>
                              </div>
                            ) : (
                              <div className="price-discounted">
                                <span className="dollar-sign">$</span>
                                {formatPrice(annualPricing.originalPrice)}
                              </div>
                            )}
                          </div>
                          <div className="pricing-option_inner_top_price_label">
                            {isAnnualDiscountActive && !isAnnualDiscountForever
                              ? 'this year'
                              : 'per year'}
                          </div>
                          {isAnnualDiscountActive &&
                            !isAnnualDiscountForever && (
                              <div className="savings">
                                <p>
                                  ${formatPrice(annualPricing.originalPrice)}{' '}
                                  per year afterwards
                                </p>
                              </div>
                            )}
                        </>
                      )}
                      {renderProCheckoutButton(
                        SubscriptionPlan.ANNUAL,
                        SubscriptionAccess.STEP_SHELF
                      )}
                    </div>
                  </div>
                  <div className="glass-wrap relative">
                    <h2>
                      Ora Pro Student Life
                      {/* The access term moved out of the removed trial note and
                        into a header tag (ORA-384). The non-trial branch still
                        names it under the price ("once · 5 years of access"). */}
                      {showFiveYearTrialMessaging && (
                        <span className="term-tag">
                          {ORA_PRO_5YEAR_YEARS}-yr access
                        </span>
                      )}
                    </h2>
                    <div className="pricing-option_inner_top_price">
                      {renderSaleBadge(
                        isFiveYearDiscountActive,
                        fiveYearPricing.badgeLabel
                      )}
                      {showFiveYearTrialMessaging ? (
                        renderTrialPriceBlock(fiveYearPricing, 'one_time')
                      ) : (
                        <>
                          <div className="pricing-option_inner_top_price_amount">
                            {isFiveYearDiscountActive ? (
                              <div className="discounted-price-container">
                                <div className="price-original">
                                  ${formatPrice(fiveYearPricing.originalPrice)}
                                </div>
                                <div className="price-discounted">
                                  <span className="dollar-sign">$</span>
                                  {formatPrice(displayFiveYearPrice)}
                                </div>
                              </div>
                            ) : (
                              <div className="price-discounted">
                                <span className="dollar-sign">$</span>
                                {formatPrice(fiveYearPricing.originalPrice)}
                              </div>
                            )}
                          </div>
                          <div className="pricing-option_inner_top_price_label">
                            once · {ORA_PRO_5YEAR_YEARS} years of access
                          </div>
                        </>
                      )}
                      {renderOneTimeCheckoutButton()}
                    </div>
                  </div>
                </div>
              )}
              <div className="pricing-option_inner_bottom">
                <div className="pricing-option_inner_bottom_features">
                  <h3>
                    <span>Features</span>
                    <span className="line"></span>
                  </h3>
                  <div className="features-container features-container--mobile">
                    <ul className="features-list">
                      {renderEverythingInFreeItem()}
                      {proFeatures.map(renderFeatureItem)}
                      {!isNclex && renderGuaranteeItem()}
                    </ul>
                  </div>
                  <div className="features-container features-container--desktop">
                    <ul className="features-list">
                      {renderEverythingInFreeItem()}
                      {proFeatures
                        .filter((feature) => !feature.subItems)
                        .map(renderFeatureItem)}
                      {!isNclex && renderGuaranteeItem()}
                    </ul>
                    {proFeaturesWithSubItems.length > 0 && (
                      <ul className="features-list">
                        {proFeaturesWithSubItems.map(renderFeatureItem)}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <img className="light-streak" src={LightStreak} alt="" />
            <img
              className="light-streak light-streak--2"
              src={LightStreak}
              alt=""
            />
            <img
              className="light-streak light-streak--3"
              src={LightStreak}
              alt=""
            />
          </div>
        </div>
        {showPromoCodeInput && (
          <div className="w-full px-4">
            <PromoCodeInput
              hasAutomaticDiscount={hasAutomaticDiscount}
              appliedPreview={promotionCodePreview}
              onApplied={(preview) => {
                setPromotionCodePreview(preview);
                setPromotionCheckoutError(null);
              }}
              onCleared={() => {
                setPromotionCodePreview(null);
                setPromotionCheckoutError(null);
              }}
            />
            {promotionCheckoutError && (
              <p
                className="mx-auto mt-2 max-w-xl text-center text-sm text-status-danger"
                role="alert"
              >
                {promotionCheckoutError}
              </p>
            )}
          </div>
        )}
        {embedded && skipFunction && (
          <div className="pricing_skip">
            <button className="button button--link" onClick={skipFunction}>
              Skip for now
              <ArrowRight />
            </button>
          </div>
        )}
        {/* The UWorld guarantee is for USMLE Step 1/2CK only; a nursing
            student never takes those exams. */}
        {!isNclex && (
          <div className="pricing_guarantee">
            <div className="pricing_guarantee_container" id="guarantee">
              <img
                className="ora-guarantee"
                src={OraGuarantee}
                alt="Ora Guarantee"
              />
              <h2>We'll Buy You UWorld If You Fail Step 1 or Step 2CK</h2>
              <p>
                If you fail USMLE Step 1 or USMLE Step 2CK after using Ora,
                we'll buy you a{' '}
                <strong>one month subscription to UWorld.</strong>
              </p>
              <p>To be eligible, you must meet the following criteria:</p>
              <ul>
                <li>You are enrolled in a United States medical school.</li>
                <li>
                  You have completed the Standard (or higher) workload (QBank
                  and flashcards) for Step 1 or Step 2CK.
                </li>
                <li>You fail Step 1 or Step 2CK.</li>
              </ul>
              <p>
                We want you to feel <strong>prepared and confident</strong>{' '}
                going into your exam. If you have any questions, please{' '}
                <button
                  className="button button--link"
                  style={{
                    display: 'inline !important',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                  onClick={() => {
                    hapticsImpactLight();
                    void showIntercom();
                  }}
                >
                  reach out to us!
                </button>
              </p>
              <div className="guarantee-footer">
                <div>
                  <a
                    href="https://www.oraai.com/terms#ora-guarantee"
                    target="_blank"
                  >
                    Read the full terms and conditions
                    <ArrowRight />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Ora AI | Pricing</title>
        <meta
          name="description"
          content="Ora AI pricing and plans for medical and nursing students. Get personalized daily study sessions, QBank, flashcards, and videos for preclinical, USMLE Step, and shelf exam prep. Start free."
        />
        <meta property="og:title" content="Ora AI | Pricing" />
        <meta
          property="og:description"
          content="Ora AI pricing and plans for medical and nursing students. Get personalized daily study sessions, QBank, flashcards, and videos for preclinical, USMLE Step, and shelf exam prep. Start free."
        />
      </Helmet>
      {embedded ? (
        renderPricing()
      ) : isAuthenticated && !showMarketingSiteStyles ? (
        <Page>{renderPricing()}</Page>
      ) : (
        <>
          <DiscountBanner />
          <MSHeader />
          <NonAuthPage>{renderPricing()}</NonAuthPage>
          <MSFooter />
        </>
      )}
    </>
  );
};

export default Pricing;
